<template>
  <div class="full_width">
    <v-btn class="refresh-button" @click="refreshOrders()">
      <v-icon color="#6E6E6E">mdi-sync</v-icon>
    </v-btn>
    <div class="table-style">
      <div class="table-header">
        <div
          v-if="isElectricitySelected"
          class="pill-radio-buttons-div electricity-type-filter"
        >
          <MultiLinePillRadioButtons
            :value="selectedElectricityTypeValue"
            :itemsList="electricityTypeValuesList"
            @input="setElectricityTypeFilter"
          />
        </div>
        <div class="pill-radio-buttons-div">
          <MultiLinePillRadioButtons
            :value="selectedStatusValue"
            :itemsList="statusValuesList"
            hasMultipleLines
            row-key="rowGroup"
            @input="statusSelection = $event"
          />
        </div>
        <div class="pill-radio-buttons-div">
          <v-text-field
            rounded
            outlined
            dense
            full-width
            v-model="searchValue"
            prepend-inner-icon="mdi-magnify"
            @input="searchOrders"
            label="Suche"
            single-line
            hide-details
          />
        </div>
      </div>
      <div v-if="serverError">
        <h1>{{ $t('order_details.loading_error') }}</h1>
        <p>{{ $t('order_details.error_hint') }}</p>
      </div>
      <v-data-table
        v-else
        disable-sort
        :headers="headersAktive"
        :items="activeOrdersDisplayed"
        item-key="orderId"
        :loading="loading"
        :loading-text="$t('overview_table.loading_text')"
        :no-data-text="
          isDataLoaded
            ? $t('overview_table.no_filter_data')
            : $t('overview_table.error_data')
        "
        :footer-props="{
          'items-per-page-options': [10],
        }"
        :server-items-length="filteredActiveOrdersCount"
        calculate-widths
        :options.sync="options"
        :page="currentPage"
      >
        <template v-slot:[`item.date`]="{ item }">
          <span>{{ item.date | formatDate('DD.MM.YYYY HH:mm') }}</span>
        </template>
        <template v-slot:[`item.status`]="props">
          <v-btn rounded dark :color="props.item.status.color">
            {{ props.item.status.label }}
          </v-btn>
        </template>
        <template v-slot:[`item.edit`]="props">
          <v-btn icon @click="showDetails(props.item.orderId)">
            <v-icon>mdi-pencil-box-outline</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import { MultiLinePillRadioButtons } from '@newnetzportal/netzportal-webapp-storybook-components';
import { getCommissioningTableConfig } from './commissioningTableConfig';
import {
  mergeOrderAddressDetails,
  needsSiteElectricityAddressDetails,
  isEventElectricityOrder,
  isSealingOrder,
} from '../clerk-orders-overview/transform_raw_data_to_orders_table_format';
import {
  commissioningTableOrderMapper,
  getCommissioningOrderType,
} from './transformRawCommissioningOrder';
import {
  getCommissioningOrderById,
  getCommissioningOrdersWithFilter,
} from '../../services/CommissioningService';
import { getCommissioningStatusDisplay } from '../../helpers/commissioningStatuses';

export default {
  name: 'CommissioningOverviewTable',
  components: {
    MultiLinePillRadioButtons,
  },
  created() {
    this.loading = true;
    this.getOrdersWithFilter();
  },
  data() {
    return {
      filterJourney: this.$route.query.journey
        ? this.$route.query.journey
        : 'all',
      statusSelection: this.$route.query.order_status
        ? this.$route.query.order_status
        : 'all',
      activeOrdersDisplayed: [],
      currentPage: this.$route.query.page
        ? parseInt(this.$route.query.page)
        : 1,
      loading: false,
      options: {},
      searchValue: this.$route.query.search ? this.$route.query.search : '',
      filteredActiveOrdersCount: 0,
      electricityTypeSelection: ['true', 'false'].includes(
        this.$route.query.feedin
      )
        ? this.$route.query.feedin
        : 'all',
      electricityTypeValuesList: getCommissioningTableConfig(
        'electricityTypeValuesList'
      ),
      statusValuesList: getCommissioningTableConfig('statusValuesList'),
      headersAktive: getCommissioningTableConfig('headersAktive'),
      isDataLoaded: false,
      serverError: false,
    };
  },
  computed: {
    journeyFilter() {
      return this.$store.getters.getJourneyFilter;
    },
    isElectricitySelected() {
      return this.journeyFilter === 'electricity';
    },
    selectedElectricityTypeValue() {
      return this.electricityTypeSelection || 'all';
    },
    selectedStatusValue() {
      return Array.isArray(this.statusSelection) || !this.statusSelection
        ? 'all'
        : this.statusSelection;
    },
  },
  watch: {
    journeyFilter(newValue) {
      this.currentPage = 1;
      this.filterJourney = newValue !== 'all' ? newValue : '';
      if (newValue !== 'electricity') {
        this.electricityTypeSelection = 'all';
        this.updateURLParams({ name: 'feedin', value: '' });
      }
      this.updateURLParams({ name: 'journey', value: this.filterJourney });
      this.getOrdersWithFilter();
    },
    $route(to) {
      if (to.path === '/inbetriebsetzung') {
        this.refreshOrders();
      }
    },
    statusSelection(newValue) {
      this.currentPage = 1;
      if (newValue !== 'all') {
        this.statusSelection = newValue;
      }
      this.updateURLParams({ name: 'order_status', value: newValue });
      this.getOrdersWithFilter();
    },
    searchValue(newValue) {
      this.updateURLParams({ name: 'search', value: newValue });
    },
    options: {
      handler(event) {
        this.updateURLParams({ name: 'page', value: event.page });
        this.currentPage = event.page;
        this.getOrdersWithFilter();
      },
      deep: true,
    },
  },
  methods: {
    updateURLParams(param) {
      const params = this.$route.query;
      if (!param.value) {
        delete params[param.name];
      } else {
        params[param.name] = param.value;
      }

      history.replaceState(
        {},
        null,
        `${this.$route.path}?${new URLSearchParams(params)}`
      );
    },
    refreshOrders() {
      const params = this.$route.query;
      params.page = 1;
      params.order_status = '';
      if (params.journey) delete params.journey;
      if (params.feedin) delete params.feedin;
      if (params.search) delete params.search;

      this.$store.commit('setJourneyFilter', 'all');
      this.statusSelection = '';
      this.electricityTypeSelection = 'all';
      this.filterJourney = '';
      this.searchValue = '';
      this.currentPage = 1;

      history.replaceState(
        {},
        null,
        `${this.$route.path}?${new URLSearchParams(params)}`
      );

      this.getOrdersWithFilter();
    },
    searchOrders(value) {
      if (value.length === 0 || value.length > 2) {
        this.currentPage = 1;
        this.getOrdersWithFilter();
      }
    },
    setElectricityTypeFilter(value) {
      this.currentPage = 1;
      this.electricityTypeSelection = value || 'all';
      this.updateURLParams({
        name: 'feedin',
        value:
          this.electricityTypeSelection === 'all'
            ? ''
            : this.electricityTypeSelection,
      });
      this.getOrdersWithFilter();
    },
    async getOrdersWithFilter() {
      const params = {
        limit: 10,
        order_status:
          this.statusSelection === 'all' ? '' : this.statusSelection,
        journey:
          !this.filterJourney || this.filterJourney === 'all'
            ? ''
            : this.filterJourney,
        journey_type:
          !this.filterJourney || this.filterJourney === 'all'
            ? ''
            : this.filterJourney,
        feedin:
          this.isElectricitySelected && this.electricityTypeSelection !== 'all'
            ? this.electricityTypeSelection
            : '',
        from_index: (this.currentPage - 1) * 10,
        search_word: this.searchValue,
      };

      this.loading = true;

      try {
        const res = await getCommissioningOrdersWithFilter(params);
        if (!res || Object.keys(res).length === 0) {
          console.warn('No commissioning orders received.');
          this.serverError = true;
          this.isDataLoaded = false;
          this.activeOrdersDisplayed = [];
          return;
        }

        this.serverError = false;
        const commissioningOrders = (res.items || []).filter(
          (order) =>
            !isEventElectricityOrder(order) &&
            !isSealingOrder(order) &&
            (order.journey_type || order.payload?.journey_type) !==
              'SITE_ELECTRIC_METER_ORDER'
        );
        const removedDedicatedOverviewOrders =
          (res.items || []).length - commissioningOrders.length;
        this.filteredActiveOrdersCount = Math.max(
          0,
          (res.count_orders || 0) - removedDedicatedOverviewOrders
        );
        const ordersWithDetails = await this.addMissingOrderDetails(
          commissioningOrders
        );
        const ordersWithStatusConfig =
          this.addStatusConfigForEachOrder(ordersWithDetails);

        this.activeOrdersDisplayed = ordersWithStatusConfig.map(
          commissioningTableOrderMapper
        );
        this.isDataLoaded = true;
      } catch (err) {
        console.error('Error fetching commissioning orders:', err);
        this.serverError = true;
        this.isDataLoaded = false;
        this.activeOrdersDisplayed = [];
      } finally {
        this.loading = false;
      }
    },
    showDetails(orderId) {
      this.$router.push({
        name: 'CommissioningDetailsPage',
        params: { id: orderId },
      });
    },
    async addMissingOrderDetails(orders) {
      return Promise.all(
        orders.map(async (order) => {
          const needsOrderType = !getCommissioningOrderType(order);
          const needsAddress = needsSiteElectricityAddressDetails(order);

          if (!order.id || (!needsOrderType && !needsAddress)) {
            return order;
          }

          const details = await getCommissioningOrderById(order.id);
          return mergeOrderAddressDetails(order, details);
        })
      );
    },
    addStatusConfigForEachOrder(ordersArray) {
      ordersArray.forEach((item) => {
        item.status = getCommissioningStatusDisplay(item.status);
      });

      return ordersArray;
    },
  },
};
</script>
